import { Pencil, Trash2, Lock, GripVertical } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { getCategoryIcon } from "./categoryUtils";
import type { Category } from "@/services/api/categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";

interface CategoryTableRowProps {
  category: Category;
  index: number;
  isDraggable: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onViewAttributes: (category: Category) => void;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
}

export function CategoryTableRow({
  category,
  index,
  isDraggable,
  isDragging,
  isDragOver,
  onEdit,
  onDelete,
  onViewAttributes,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: CategoryTableRowProps) {
  const attributesCount =
    category.attributes?.length ?? category.attributesCount ?? 0;
  const isLocked = category.isLocked || false;
  const IconComponent = getCategoryIcon(category.icon ?? "Pencil");

  return (
    <tr
      key={String(category.id)}
      draggable={isDraggable}
      onDragStart={() => isDraggable && onDragStart(index)}
      onDragOver={(e) => isDraggable && onDragOver(e, index)}
      onDrop={(e) => isDraggable && onDrop(e, index)}
      onDragEnd={onDragEnd}
      className={`transition-colors ${
        isDragging
          ? "opacity-50 bg-grid-100"
          : isDragOver
            ? "bg-imperial-50 border-t-2 border-imperial-400"
            : "hover:bg-grid-50"
      }`}
    >
      {isDraggable && (
        <td className="w-10 px-2 py-4">
          <GripVertical className="h-5 w-5 text-ink-400 cursor-grab active:cursor-grabbing" />
        </td>
      )}
      <td className="px-6 py-4">
        <div className="font-medium text-ink-900">{getCategoryLabel(category)}</div>
      </td>
      <td className="px-6 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-grid-200 bg-white">
          {IconComponent ? (
            <IconComponent className="h-5 w-5 text-ink-600" />
          ) : (
            <div className="h-3 w-3 rounded-full bg-ink-300" />
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <button
          onClick={() => onViewAttributes(category)}
          className="text-sm text-ink-600 hover:text-imperial-600 hover:underline"
        >
          {attributesCount} attribute{attributesCount !== 1 ? "s" : ""} linked
        </button>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(category)}
            className="h-8 w-8"
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          {isLocked ? (
            <Button
              variant="ghost"
              size="icon"
              disabled
              className="h-8 w-8"
              title="This category is locked and cannot be deleted"
            >
              <Lock className="h-4 w-4 text-ink-400" />
              <span className="sr-only">Locked</span>
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(category)}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
